import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { PieChart } from 'react-minimal-pie-chart'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { selectRoomSession, setRoomSession } from '../../../../../store/meetingSummary/roomSession'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { MEETING_SCORE_PRESET_PRESET_ID, ScoreResult } from './model'
import { getPresetResultsByInputParameterHierarchy, parseMetadata } from '../utils'
import { LoadingResult } from '../MeetingResults/ResultCard/Body/LoadingResult'
import './MeetingScorecard.scss'
import { AiResult, StructuredSummaryResult } from '../../../../../models/AiResult'
import { MeetingScorecardSection } from './MeetingScorecardSection'
import { MeetingScoreItems } from './MeetingScoreItems'
import { ReactComponent as WorkFlowIcon } from '../../../../../assets/images/workflow-score.svg'
import { ReactComponent as EngagementIcon } from '../../../../../assets/images/engagement-score.svg'
import { selectExecutedAiFeatures, stopExecutedAiFeaturesPolling } from '../../../../../store/meetingSummary/executedAiFeatures'
import { selectAiResultPollingEnabled, selectAiResults, startAiResultPolling, stopAiResultPolling } from '../../../../../store/meetingSummary/aiResults'
import { useGetFeaturesQuery } from '../../../../../store/ai/ai'
import { selectSelectedMeetingTemplate } from '../../../../../store/meetingSummary/meetingTemplates'
import { selectInputParametersHierarchyForSelectedMeetingTemplate } from '../../../../../store/meetingSummary/inputParametersHierarchy'
import { useLazyGetRoomSessionQuery } from '../../../../../store/media/media'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { NavLink } from 'react-router-dom'

import { MeetingScorecardDisabled } from './MeetingScorecardDisabled'
import { useLazyGetUserByIdQuery } from '../../../../../store/user/user'
import { MeetingScorecardManualRun } from './MeetingScorecardManualRun'

export const MeetingScorecard: React.FC = () => {
  const roomSession = useAppSelector(selectRoomSession)
  const executedAiFeatures = useAppSelector(selectExecutedAiFeatures)
  const allAiResults = useAppSelector(selectAiResults)
  const accountId = useAppSelector(selectAccountId)
  const meetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const inputParametersHierarchy = useAppSelector(selectInputParametersHierarchyForSelectedMeetingTemplate)
  const dispatch = useAppDispatch()
  const [triggerGetRoomSession] = useLazyGetRoomSessionQuery()
  const [triggerGetUserById] = useLazyGetUserByIdQuery()
  const { data: account } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: true })
  const [scoreText, setScoreText] = useState<string>('')
  const areAiResultsPolling = useAppSelector(selectAiResultPollingEnabled)
  const [scoreResult, setScoreResult] = useState<ScoreResult | null>(null)
  const [selectedResult, setSelectedResult] = useState<AiResult | null>(null)
  const meetingScorecardEnabled = account?.data.effectiveConfig.meetingScorecard ?? false

  const refreshRoomSession = useCallback(async () => {
    if (roomSession?.roomId && roomSession?.id) {
      const response = await triggerGetRoomSession({
        accountId,
        roomId: roomSession.roomId,
        sessionId: roomSession.id,
      })

      if (response?.data?.data) {
        dispatch(setRoomSession(response.data.data))
      }
    }
  }, [accountId, dispatch, roomSession?.id, roomSession?.roomId, triggerGetRoomSession])

  const getAdvisorName = (): string => {
    if (!roomSession?.roomCreatedBy || roomSession.roomCreatedBy.type !== 'user') {
      return ''
    }

    const createdById = roomSession.roomCreatedBy.id
    if (!createdById) {
      return ''
    }

    const user = roomSession.users.find((user) => user.id === createdById)
    if (!user) {
      triggerGetUserById({ accountId, userId: createdById }).then((response) => {
        if (response.data) {
          return response.data.data?.firstName || ''
        }
      })
    }
    return user?.firstName || ''
  }

  const getScoreText = (zocksScore: number) => {
    const name = getAdvisorName()
    const nameText = name ? `, ${name}` : ''
    if (zocksScore >= 90) {
      return `Flawless Execution${nameText}!`
    } else if (zocksScore >= 60) {
      return `Excellent job, keep it up${nameText}!`
    } else if (zocksScore >= 40) {
      return `Promising, but room to grow${nameText}!`
    } else {
      return `Let's improve together${nameText}!`
    }
  }

  const getColor = (zocksScore: number) => {
    if (zocksScore >= 60) {
      return '#28a745'
    } else if (zocksScore >= 40) {
      return '#ffc107'
    } else {
      return '#cd2a3a'
    }
  }

  const { data: featureData, isFetching: isFetchingFeatureData } = useGetFeaturesQuery({
    accountId: accountId,
    pageRequest: { page: 0, size: 100 },
    aiFeaturePresetIds: MEETING_SCORE_PRESET_PRESET_ID,
    includeRestricted: true,
  })

  const executedScorecard = executedAiFeatures.find((feature) => feature.presetId === MEETING_SCORE_PRESET_PRESET_ID)

  const aiFeatureWasExecutedWithInputParameter =
    featureData?.data[0] &&
    inputParametersHierarchy &&
    executedAiFeatures.some((executedFeature) => {
      const { id: executedAiFeatureId, presetId: MEETING_SCORE_PRESET_PRESET_ID, inputParametersHierarchyId: executedInputParameterId = null } = executedFeature
      const inputParameterHierarchyIdForPreset = inputParametersHierarchy.inputParametersHierarchyNodeByPresets[MEETING_SCORE_PRESET_PRESET_ID]?.id ?? null
      const updatedAt = inputParametersHierarchy.inputParametersHierarchyNodeByPresets?.[MEETING_SCORE_PRESET_PRESET_ID]?.updatedAt ?? null
      const inputParameterChanged = updatedAt ? updatedAt > executedFeature.timestamp : false
      return executedAiFeatureId === featureData?.data[0].id && executedInputParameterId === inputParameterHierarchyIdForPreset && !inputParameterChanged
    })

  const aiResults = useMemo(() => {
    if (!featureData?.data[0] || !inputParametersHierarchy || !allAiResults) {
      return []
    }

    const { inputParametersHierarchyNodeByPresets } = inputParametersHierarchy
    const presetAiResults = getPresetResultsByInputParameterHierarchy(
      MEETING_SCORE_PRESET_PRESET_ID,
      featureData?.data[0].id,
      allAiResults,
      inputParametersHierarchyNodeByPresets,
    )

    const presetAiResultsWithoutNoResult = presetAiResults.filter(
      (r) =>
        r.status !== 'no_result' &&
        r.inputParametersHierarchyId === inputParametersHierarchy.inputParametersHierarchyNodeByPresets[MEETING_SCORE_PRESET_PRESET_ID]?.id,
    )
    return presetAiResultsWithoutNoResult.length ? presetAiResultsWithoutNoResult : presetAiResults
  }, [featureData?.data, inputParametersHierarchy, allAiResults])

  useEffect(() => {
    if (!aiResults.length) {
      return
    }
    const result = aiResults.find((result) => result.aiFeatureId === executedScorecard?.id)
    if (result && result.result) {
      setSelectedResult(result)
      const scoreResult = (result?.result as StructuredSummaryResult).result as any
      const scoreInsights = scoreResult as ScoreResult
      setScoreResult(scoreInsights)
      refreshRoomSession()
    }
  }, [aiResults, executedScorecard?.id, refreshRoomSession, meetingTemplate])

  const zocksScore = useMemo(() => {
    if (!roomSession) {
      return null
    }
    const metadata = parseMetadata(roomSession.metadata)
    const externalSessionMetadata = metadata?.external ?? {}
    const id = meetingTemplate?.id
    if (id) {
      return externalSessionMetadata?.meetingScore?.[id] ?? null
    }
    return null
  }, [meetingTemplate?.id, roomSession])

  useEffect(() => {
    setScoreText(getScoreText(zocksScore))
  }, [zocksScore])

  const handleManualRun = useCallback(() => {
    setSelectedResult(null)
    setScoreResult(null)
  }, [])

  useEffect(() => {
    if (aiFeatureWasExecutedWithInputParameter && !scoreResult) {
      dispatch(startAiResultPolling())
    } else if (scoreResult && aiFeatureWasExecutedWithInputParameter && areAiResultsPolling) {
      dispatch(stopAiResultPolling())
      dispatch(stopExecutedAiFeaturesPolling())
    }
  }, [aiFeatureWasExecutedWithInputParameter, dispatch, scoreResult, areAiResultsPolling])

  useEffect(() => {
    const shouldPoll = roomSession?.roomId && roomSession?.id && aiFeatureWasExecutedWithInputParameter && scoreResult && !zocksScore

    if (!shouldPoll) {
      return
    }
    refreshRoomSession()
    const intervalId = setInterval(refreshRoomSession, 8000)
    return () => clearInterval(intervalId)
  }, [roomSession?.roomId, roomSession?.id, aiFeatureWasExecutedWithInputParameter, scoreResult, accountId, zocksScore, dispatch, refreshRoomSession])

  if (!meetingScorecardEnabled) {
    return <MeetingScorecardDisabled />
  }
  if (!featureData && !isFetchingFeatureData) {
    return <div className='paragraph-medium-extended zocks-text-gray-500 py-1 pe-4 ps-4'>Meeting scorecard configuration is not set, contact support.</div>
  }

  if (!executedAiFeatures.length) {
    return <LoadingResult />
  }

  if (!allAiResults.find((result) => result && result.aiFeatureId === featureData?.data[0].id) && aiFeatureWasExecutedWithInputParameter) {
    return <LoadingResult />
  }

  if (!zocksScore || !scoreResult || !aiFeatureWasExecutedWithInputParameter) {
    if (!featureData?.data[0]) {
      return <div className='paragraph-medium-extended zocks-text-gray-500 py-1 pe-4 ps-4'>Meeting scorecard configuration is not set, contact support</div>
    }
    return <MeetingScorecardManualRun aiFeature={featureData?.data[0]} onManualRun={handleManualRun} />
  }

  const scoreSections = [
    {
      type: 'Scheduling',
      icon: <i className='bi bi-clock zocks-text-white icon-size'></i>,
      scoreResult: scoreResult?.Scheduling,
      description: 'Evaluates scheduling efficiency with insights on time allocation, meeting sources, and follow-up bookings.',
      values: [
        {
          title: 'Time Allocation',
          description: 'Stayed within 10% of scheduled time',
          value: scoreResult?.Scheduling?.values?.overUnderTime ?? null,
          key: 'overUnderTime',
          infoNeeded: false,
          islastRow: false,
        },
        {
          title: 'Meeting Source',
          description: 'Event booked in advance, invite in the calendar',
          value: scoreResult?.Scheduling?.values?.calendarEvent ?? null,
          key: 'calendarEvent',
          infoNeeded: false,
          islastRow: false,
        },
        {
          title: 'Follow-up Meeting',
          description: 'Next session booked in advance',
          value: scoreResult?.Scheduling?.values?.futureMeetings ?? null,
          key: 'futureMeetings',
          infoNeeded: false,
          islastRow: true,
        },
      ],
    },
    {
      type: 'Engagement',
      icon: <EngagementIcon className='zocks-text-white icon-size' />,
      scoreResult: scoreResult?.Engagement,
      description:
        'Measures client engagement through talk time, sentiment, interactivity, and conversation balance, with customizable insights on connection and personal engagement.',
      values: [
        {
          title: 'Client Connection',
          description: 'Checks if key topics on rapport building—or a custom theme—were covered.',
          value: scoreResult?.Engagement?.values?.clientConnection ?? null,
          key: 'clientConnection',
          infoNeeded: true,
          islastRow: false,
        },
        {
          title: 'Personal Engagement',
          description: 'Checks if key topics like family and hobbies—or a custom theme—were covered.',
          value: scoreResult?.Engagement?.values?.personalEngagement ?? null,
          key: 'personalEngagement',
          infoNeeded: true,
          islastRow: false,
        },
        {
          title: 'Client Talk Time',
          description: 'Clients spoke at least 30% of the meeting',
          value: scoreResult?.Engagement?.values?.avgTalkTime ?? null,
          key: 'avgTalkTime',
          infoNeeded: false,
          islastRow: false,
        },
        {
          title: 'Client Sentiment',
          description: 'Overall client sentiment score is above 5',
          value: scoreResult?.Engagement?.values?.sentiment ?? null,
          key: 'sentiment',
          infoNeeded: false,
          islastRow: false,
        },
        {
          title: 'Client Questions',
          description: 'At least 3 questions were asked',
          value: scoreResult?.Engagement?.values?.clientQuestions ?? null,
          key: 'clientQuestions',
          infoNeeded: false,
          islastRow: false,
        },
        {
          title: 'Balanced Conversation',
          description: 'Avoided long advisor monologues',
          value: scoreResult?.Engagement?.values?.avoidedLongMonologue ?? null,
          key: 'avoidedLongMonologue',
          infoNeeded: false,
          islastRow: false,
        },
        {
          title: 'Question Rate',
          description: 'Determines if the advisor asked at least one question in 5 or more of the 10 meeting sections.',
          value: scoreResult?.Engagement?.values?.questionRate ?? null,
          key: 'questionRate',
          infoNeeded: false,
          islastRow: false,
        },
        {
          title: 'Interactivity Score',
          description: 'Checks if the client reacted in at least 30% of the meeting, based on 10 equal sections.',
          value: scoreResult?.Engagement?.values?.interactiveConversation ?? null,
          key: 'interactiveConversation',
          infoNeeded: false,
          islastRow: true,
        },
      ],
    },
    {
      type: 'Workflow',
      icon: <WorkFlowIcon className='zocks-text-white icon-size' />,
      scoreResult: scoreResult?.Workflow,
      description:
        'Tracks meeting effectiveness with insights on decision-making, agenda use, next steps, tasks, life events, and customizable core motivators.',
      values: [
        {
          title: 'Core Motivators',
          description: 'Checks if key topics like dreams, fears, and goals—or a custom theme—were covered.',
          value: scoreResult?.Workflow?.values?.coreMotivators ?? null,
          key: 'coreMotivators',
          infoNeeded: true,
          islastRow: false,
        },
        {
          title: 'Next Steps',
          description: 'Steps clearly discussed',
          value: scoreResult?.Workflow?.values?.nextSteps ?? null,
          key: 'nextSteps',
          infoNeeded: false,
          islastRow: false,
        },
        {
          title: 'Life Events',
          description: 'Discussed life events',
          value: scoreResult?.Workflow?.values?.lifeEvents ?? null,
          key: 'lifeEvents',
          infoNeeded: false,
          islastRow: false,
        },
        {
          title: 'Tasks Identified',
          description: 'Follow-up tasks collected',
          value: scoreResult?.Workflow?.values?.followUpTasks ?? null,
          key: 'followUpTasks',
          infoNeeded: false,
          islastRow: false,
        },
        {
          title: 'Agenda',
          description: 'Tracks if agenda was presented',
          value: scoreResult?.Workflow?.values?.meetingAgenda ?? null,
          key: 'meetingAgenda',
          infoNeeded: false,
          islastRow: false,
        },
        {
          title: 'Agreed Decision',
          description: 'At least 1 decision agreed to',
          value: scoreResult?.Workflow?.values?.decisionsAgreed ?? null,
          key: 'decisionsAgreed',
          infoNeeded: false,
          islastRow: false,
        },
        {
          title: 'Confirmed consensus',
          description: 'Flags if disagreement was detected',
          value: scoreResult?.Workflow?.values?.decisionsNotAgreed ?? null,
          key: 'decisionsNotAgreed',
          infoNeeded: false,
          islastRow: true,
        },
      ],
    },
  ]

  if (selectedResult?.status === 'no_result') {
    if (!featureData?.data[0]) {
      return <div className='paragraph-medium-extended zocks-text-gray-500 py-1 pe-4 ps-4'>Meeting scorecard configuration is not set, contact support</div>
    }
    return <MeetingScorecardManualRun aiFeature={featureData?.data[0]} onManualRun={handleManualRun} />
  }

  return meetingTemplate && meetingTemplate.id && scoreResult ? (
    <Container className='w-100 mw-100 p-0'>
      <div className='score-container-center'>
        <Row className='pt-5'>
          <Col s='auto' lg='3' className='sticky-lg-up custom-sticky-col pb-4'>
            <div className='zocks-p-2.0 gap-4 align-items-start border zocks-border-1.0 zocks-border-color-gray-300 h-25'>
              <h3 className='text-center zocks-text-gray-900'> {scoreText} </h3>
              <div className='border zocks-border-1.0 zocks-border-color-gray-100 zocks-bg-color-gray-100 gap-3 p-4 mt-4'>
                <Row className='d-flex gap-2'>
                  <Col className='d-flex flex-column align-items-center position-relative'>
                    <PieChart
                      data={[
                        { title: 'Score', value: zocksScore, color: getColor(zocksScore) },
                        { title: 'Remaining', value: 100 - zocksScore, color: '#E9ECEF' },
                      ]}
                      lineWidth={30}
                      totalValue={100}
                      startAngle={270}
                      className='chart-size'
                    />
                    <div className='position-absolute d-flex flex-column align-items-center justify-content-center w-100 h-100'>
                      <h1 className='zocks-text-gray-900 mb-0'>{zocksScore}</h1>
                      <div className='lead-small zocks-text-gray-900'>Zocks Score</div>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
            <div className='border zocks-border-1.0 zocks-border-primary-light zocks-bg-color-primary-light gap-2 p-3 mt-4 d-flex align-items-start'>
              <Col xs='auto'>
                <div className='p-1 zocks-bg-color-primary-light rounded zocks-text-primary-dark'>
                  <i className='meetingTemplateIcon d-flex bi bi-columns-gap fs-5' />
                </div>
              </Col>
              <Col>
                <div className='paragraph-medium bold zocks-text-primary-dark'>Make this score work for you</div>
                <div className='paragraph-medium zocks-text-primary-dark'>
                  <span>Adjust certain scoring criteria in the </span>
                  <NavLink
                    className='button-link-small zocks-text-primary-dark p-0'
                    to={`/workspace/setup/meeting-types/meeting-type/details/${meetingTemplate?.id}/presets/scorecard`}
                  >
                    Meeting Type settings
                  </NavLink>
                  <span> to match your approach.</span>
                </div>
              </Col>
            </div>
          </Col>
          <Col>
            {scoreSections.map(
              ({ type, icon, scoreResult, values, description }) =>
                scoreResult &&
                scoreResult.improve &&
                scoreResult.strength &&
                values && (
                  <MeetingScorecardSection
                    key={type}
                    type={type}
                    icon={icon}
                    description={description}
                    scoreResult={scoreResult}
                    meetingScoreItems={
                      <div className='w-100'>
                        {values.map(({ title, description, value, key, infoNeeded, islastRow }) => (
                          <MeetingScoreItems
                            key={key}
                            title={title}
                            description={description}
                            value={value}
                            valueKey={key}
                            infoNeeded={infoNeeded}
                            islastRow={islastRow}
                          />
                        ))}
                      </div>
                    }
                  />
                ),
            )}
          </Col>
        </Row>
      </div>
    </Container>
  ) : (
    <div></div>
  )
}
